import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UserCheck, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function Avatar({ src, name }) {
  const initials = (name || "?")[0]?.toUpperCase();
  return src ? (
    <img src={src} alt="" className="w-11 h-11 rounded-full object-cover border border-[#E5E5E5]" />
  ) : (
    <div className="w-11 h-11 rounded-full bg-[#E5E5E5] text-[#999] flex items-center justify-center text-sm font-bold">{initials}</div>
  );
}

export default function FollowRequestsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/follow-requests?area=SOCIAL`);
      setRequests(data.requests || []);
      window.dispatchEvent(new Event("follow-requests-refresh"));
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (request_id, action) => {
    setBusyId(request_id);
    try {
      await api.post(`/me/follow-requests/${request_id}/${action}`);
      setRequests((rs) => rs.filter((r) => r.request_id !== request_id));
      toast.success(action === "approve"
        ? t("follow_requests.approved", { defaultValue: "Angenommen" })
        : t("follow_requests.rejected", { defaultValue: "Abgelehnt" }));
      window.dispatchEvent(new Event("follow-requests-refresh"));
      window.dispatchEvent(new Event("watch-unread-refresh"));
    } catch {
      toast.error(t("follow_requests.action_failed", { defaultValue: "Aktion fehlgeschlagen" }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="follow-requests-page" className="w-full max-w-2xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <div className="flex items-center gap-3 px-4 mb-3">
        <button data-testid="follow-req-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2 flex-1">
          <UserCheck size={20} /> {t("follow_requests.title", { defaultValue: "Follow-Anfragen" })}
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#999]" /></div>
      ) : requests.length === 0 ? (
        <p data-testid="follow-req-empty" className="px-4 py-16 text-center text-sm text-[#999]">
          {t("follow_requests.empty", { defaultValue: "Keine offenen Anfragen." })}
        </p>
      ) : (
        <div className="divide-y divide-[#EEE]">
          {requests.map((r) => (
            <div key={r.request_id} data-testid={`follow-req-${r.request_id}`} className="flex items-center gap-3 px-4 py-3">
              <Avatar src={r.avatar_url} name={r.display_name} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0A0A0A] truncate">{r.display_name || "—"}</p>
                {r.handle && <p className="text-xs text-[#999] truncate">@{r.handle}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid={`follow-req-approve-${r.request_id}`}
                  disabled={busyId === r.request_id}
                  onClick={() => act(r.request_id, "approve")}
                  className="h-9 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1 hover:bg-[#222] transition-colors disabled:opacity-50"
                >
                  <Check size={14} /> {t("follow_requests.approve", { defaultValue: "Annehmen" })}
                </button>
                <button
                  data-testid={`follow-req-reject-${r.request_id}`}
                  disabled={busyId === r.request_id}
                  onClick={() => act(r.request_id, "reject")}
                  className="h-9 w-9 rounded-full border border-[#E5E5E5] text-[#666] flex items-center justify-center hover:bg-[#F5F5F5] transition-colors disabled:opacity-50"
                  aria-label={t("follow_requests.reject", { defaultValue: "Ablehnen" })}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
